import { computed } from 'mobx'
import { type Checklist } from '@/features/jobs/info-gathering/Checklist'
import { type Member } from '@/features/iam/members/Member'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { CompleteInfoGatheringDialogViewModel } from '@/features/jobs/details/components/InfoGatheringCard/CompleteInfoGatheringDialog/CompleteInfoGatheringDialogViewModel'

/**
 * The card a job shows while its info is being gathered.
 */
export class InfoGatheringCardViewModel {
  readonly completeInfoGatheringDialogVm

  constructor(
    private readonly _job: Job,
    createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getChecklist: () => Checklist | undefined,
    private readonly _getJobWorkspaceMember: () => Member,
    jobStore: JobStore,
  ) {
    this.completeInfoGatheringDialogVm =
      new CompleteInfoGatheringDialogViewModel(
        _job,
        jobStore,
        createAlertErrorHandler,
      )
  }

  /**
   * Whether the current user can update the job as an admin.
   */
  @computed
  get isAdmin() {
    return this._getJobWorkspaceMember().permissions.adminJobUpdate
  }

  /**
   * Whether the current user can update the workspace's info gathering.
   */
  @computed
  get isFirmAlly() {
    return this._getJobWorkspaceMember().permissions
      .workspaceJobInfoGatheringUpdate
  }

  /**
   * Whether the current user is a client on the job.
   */
  @computed
  get isClient() {
    return this._job.currentUserCollaborator?.type === 'Client'
  }

  /**
   * Whether the current user can complete info gathering now.
   */
  @computed
  get canCompleteInfoGathering() {
    if (this.isAdmin) {
      return true
    }

    const checklist = this._getChecklist()
    const allRequirementsCompleted = !!checklist?.sections.every(
      (s) => s.requirementsCompleted,
    )

    return allRequirementsCompleted && (this.isClient || this.isFirmAlly)
  }

  /**
   * The card's title.
   */
  @computed
  get title() {
    const consultation = this._job.consultation
    return consultation?.isPendingBooking || consultation?.isPendingAvailability
      ? 'Awaiting for customer availability'
      : 'Gather Supporting Items'
  }

  /**
   * What the card asks of the current user.
   */
  @computed
  get description() {
    const consultation = this._job.consultation
    if (this.isAdmin) {
      if (consultation?.isPendingAvailability) {
        return consultation.isPendingBooking
          ? 'Pending consultation scheduling'
          : 'Customer availability selection pending'
      }
      return 'Awaiting supporting items from the client. To proceed, complete on their behalf'
    }

    return this.isClient
      ? 'Awaiting supporting items. To proceed, please provide the requested items found in the checklist.'
      : 'Awaiting supporting items from the client.'
  }

  /**
   * Whether info gathering can be completed before the consultation's
   * availability is picked. Tax jobs don't wait on it.
   */
  @computed
  get isCompletionOpen() {
    return (
      !this._job.consultation?.isPendingAvailability ||
      this._job.jobType?.type === 'Tax'
    )
  }

  /**
   * Whether the card offers completing info gathering to the current user,
   * rather than showing whom it waits on.
   */
  @computed
  get canOfferCompletion() {
    return (
      this.isCompletionOpen &&
      (this.isAdmin || this.isClient || this.canCompleteInfoGathering)
    )
  }

  /**
   * The pro the client requested, if any.
   */
  @computed
  get requestedPro() {
    return this._job.requestedPro
  }
}
